import React, { useEffect, useState } from "react";
import { Plus, Play, Pause, Pencil, Users, BarChart3, Loader2, ChevronRight, ChevronLeft, Zap, Clock, PhoneCall, FileSpreadsheet } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { DirectionBadge } from "@/lib/format";
import { LeadUploadPanel } from "@/components/LeadUploadPanel";
import api, { apiErr } from "@/lib/api";
const STEPS = ["Basics", "Lead Source", "Calling Rules", "Inbound Config", "AI Settings", "Post-Call", "Review"];

function Wizard({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", description: "", direction: "outbound", status: "active",
    lead_source_type: "excel_csv", language: "auto", max_attempts: 3, retry_gap_hours: 1,
    outbound_greeting: "Namaste {name} ji! Main Akriti Patel hoon Anton Automation se. Aapne {interest} ke liye enquiry ki thi.",
    inbound_greeting: "Namaste! Anton Automation mein aapka swagat hai. Main Akriti hoon.",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isOut = f.direction === "outbound" || f.direction === "both";
  const isIn = f.direction === "inbound" || f.direction === "both";

  const visibleSteps = STEPS.filter((s, i) => {
    if (["Lead Source", "Calling Rules"].includes(s)) return isOut;
    if (s === "Inbound Config") return isIn;
    return true;
  });
  const cur = visibleSteps[step];

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/campaigns", {
        name: f.name, description: f.description, direction: f.direction, status: "active",
        language: f.language, max_attempts: Number(f.max_attempts), retry_gap_hours: Number(f.retry_gap_hours),
        lead_source_type: isOut ? f.lead_source_type : "inbound",
        calling_windows: isOut ? [{ start: "10:00", end: "13:00" }] : [],
        post_call_config: {},
      });
      toast.success("Campaign created");
      onCreated();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="min-w-0 w-full">
      <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2 min-w-0">
        {visibleSteps.map((s, i) => (
          <div key={s} className={`flex items-center gap-1 text-xs whitespace-nowrap shrink-0 ${i === step ? "text-indigo-600 font-semibold" : i < step ? "text-emerald-600" : "text-slate-400"}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${i === step ? "bg-indigo-600 text-white" : i < step ? "bg-emerald-500 text-white" : "bg-slate-200 dark:bg-slate-700"}`}>{i + 1}</span>
            {s}{i < visibleSteps.length - 1 && <ChevronRight className="w-3 h-3" />}
          </div>
        ))}
      </div>

      <div className="min-h-[280px]">
        {cur === "Basics" && (
          <div className="space-y-4">
            <div><Label>Campaign Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Indiamart Leads Q3" data-testid="wizard-name" /></div>
            <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
            <div><Label>Direction</Label>
              <Select value={f.direction} onValueChange={(v) => { set("direction", v); setStep(0); }}>
                <SelectTrigger data-testid="wizard-direction"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="outbound">📤 Outbound</SelectItem><SelectItem value="inbound">📥 Inbound</SelectItem><SelectItem value="both">↕️ Both</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        )}
        {cur === "Lead Source" && (
          <div className="space-y-4">
            <Label>Lead Source</Label>
            <div className="grid grid-cols-2 gap-3">
              {[["excel_csv", "Excel / CSV Upload"], ["manual", "Manual Entry"]].map(([v, l]) => (
                <button key={v} onClick={() => set("lead_source_type", v)} className={`p-4 rounded-lg border text-left text-sm font-medium ${f.lead_source_type === v ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10" : "hover:bg-accent"}`}>{l}</button>
              ))}
            </div>
            {f.lead_source_type === "excel_csv" && (
              <div className="text-xs text-slate-500 flex items-start gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border">
                <FileSpreadsheet className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                <span>After creating the campaign, use <b>Import Leads (Excel/CSV)</b> on the Campaigns page to upload your leads with our template.</span>
              </div>
            )}
          </div>
        )}
        {cur === "Calling Rules" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Max Attempts</Label><Input type="number" min={1} max={5} value={f.max_attempts} onChange={(e) => set("max_attempts", e.target.value)} /></div>
              <div><Label>Retry Gap (hrs)</Label><Input type="number" min={1} max={24} value={f.retry_gap_hours} onChange={(e) => set("retry_gap_hours", e.target.value)} /></div>
            </div>
            <div><Label>Calling Windows (IST)</Label><div className="flex gap-2 mt-1"><Input defaultValue="10:00" /><span className="self-center">to</span><Input defaultValue="13:00" /></div></div>
            <div className="text-xs text-slate-500">Skip weekends & Indian public holidays applied by default.</div>
          </div>
        )}
        {cur === "Inbound Config" && (
          <div className="space-y-4">
            <div><Label>Plivo Number</Label><Input defaultValue="+912269124245" /></div>
            <div className="grid grid-cols-2 gap-4"><div><Label>Business Start</Label><Input defaultValue="09:00" /></div><div><Label>Business End</Label><Input defaultValue="19:00" /></div></div>
            <div><Label>After-hours Handling</Label><Select defaultValue="whatsapp"><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="voicemail">Voicemail</SelectItem><SelectItem value="whatsapp">WhatsApp auto-reply</SelectItem><SelectItem value="callback">Callback scheduling</SelectItem></SelectContent></Select></div>
            <div><Label>Max Concurrent Inbound</Label><Input type="number" defaultValue={3} /></div>
          </div>
        )}
        {cur === "AI Settings" && (
          <div className="space-y-4">
            <div><Label>Language</Label><Select value={f.language} onValueChange={(v) => set("language", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="auto">Auto-detect (recommended)</SelectItem><SelectItem value="hi-IN">Hindi</SelectItem><SelectItem value="gu-IN">Gujarati</SelectItem></SelectContent></Select></div>
            {isOut && <div><Label>Outbound Greeting</Label><Textarea value={f.outbound_greeting} onChange={(e) => set("outbound_greeting", e.target.value)} /></div>}
            {isIn && <div><Label>Inbound Greeting</Label><Textarea value={f.inbound_greeting} onChange={(e) => set("inbound_greeting", e.target.value)} /></div>}
            <div className="text-xs text-slate-500">Same 5 qualification criteria apply to both directions.</div>
          </div>
        )}
        {cur === "Post-Call" && (
          <div className="space-y-3 text-sm">
            {["WhatsApp templates per outcome", "Email nurture for outbound no-answers", "Callback scheduling for inbound missed"].map((t) => (
              <label key={t} className="flex items-center gap-2 p-3 rounded-lg border"><input type="checkbox" defaultChecked className="accent-indigo-600" /> {t}</label>
            ))}
          </div>
        )}
        {cur === "Review" && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between border-b py-2"><span className="text-slate-500">Name</span><span className="font-medium">{f.name || "—"}</span></div>
            <div className="flex justify-between border-b py-2"><span className="text-slate-500">Direction</span><DirectionBadge direction={f.direction} /></div>
            <div className="flex justify-between border-b py-2"><span className="text-slate-500">Language</span><span>{f.language}</span></div>
            {isOut && <div className="flex justify-between border-b py-2"><span className="text-slate-500">Source</span><span>{f.lead_source_type}</span></div>}
            <div className="flex justify-between border-b py-2"><span className="text-slate-500">Max Attempts</span><span>{f.max_attempts}</span></div>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}><ChevronLeft className="w-4 h-4" /> Back</Button>
        {step < visibleSteps.length - 1 ? (
          <Button onClick={() => setStep((s) => s + 1)} disabled={step === 0 && !f.name} data-testid="wizard-next">Next <ChevronRight className="w-4 h-4" /></Button>
        ) : (
          <Button onClick={create} disabled={saving} data-testid="wizard-launch">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Launch Campaign"}</Button>
        )}
      </div>
    </div>
  );
}

function AutoDialerCard() {
  const [st, setSt] = useState(null);
  const [running, setRunning] = useState(false);
  const load = () => api.get("/scheduler/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const runNow = async () => { setRunning(true); try { const { data } = await api.post("/scheduler/run-now"); toast.success(data.reason || `Dispatched ${data.dispatched}`); load(); } catch (e) { toast.error(apiErr(e)); } finally { setRunning(false); } };
  const toggle = async (v) => { await api.patch("/scheduler/settings", { enabled: v }); toast.success(`Auto-dialer ${v ? "enabled" : "paused"}`); load(); };
  if (!st) return null;
  return (
    <Card className="border shadow-none p-5 mb-6 bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-500/5 dark:to-transparent" data-testid="auto-dialer-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/30"><Zap className="w-5 h-5 text-white" /></div>
        <div className="flex-1">
          <div className="flex items-center gap-2"><h3 className="font-semibold font-display">Auto Dialer</h3><span className={`text-xs px-2 py-0.5 rounded-full ${st.enabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15" : "bg-slate-200 text-slate-500"}`}>{st.enabled ? "Active" : "Paused"}</span></div>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Runs every {st.interval_min} min during IST windows · {st.pending_eligible} eligible leads · {st.last_reason}</p>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={st.enabled} onCheckedChange={toggle} data-testid="dialer-toggle" />
          <Button size="sm" variant="outline" onClick={runNow} disabled={running} data-testid="dialer-run-now">{running ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}Run Now</Button>
        </div>
      </div>
    </Card>
  );
}

function TestCallWidget() {
  const [phone, setPhone] = useState("");
  const [calling, setCalling] = useState(false);
  const [last, setLast] = useState(null);
  const [lead, setLead] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/campaigns/test-lead").then((r) => {
      const l = r.data?.lead;
      if (l) { setLead(l); setPhone(l.mobile || ""); }
    }).catch(() => {});
  }, []);

  const call = async () => {
    const num = (phone || lead?.mobile || "").trim();
    if (!num) { toast.error("No lead phone number available"); return; }
    setCalling(true); setLast(null); setDetail(null);
    try {
      const { data } = await api.post("/agent/test-call", { phone: num, lead_id: lead?.id });
      console.log("[DIAL-OUT] request body sent to Railway:", data.request);
      console.log("[DIAL-OUT] Railway response:", { status: data.railway_status, body: data.railway_response });
      setLast(data.message);
      setDetail({ request: data.request, status: data.railway_status, response: data.railway_response, ok: data.dispatched });
      (data.dispatched ? toast.success : toast.error)(data.message);
    } catch (e) { toast.error(apiErr(e)); } finally { setCalling(false); }
  };
  return (
    <Card className="border shadow-none p-5 mb-6" data-testid="campaign-test-call-widget" style={{ borderColor: "rgba(79,70,229,0.35)", background: "rgba(79,70,229,0.04)" }}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/30 shrink-0"><PhoneCall className="w-5 h-5 text-white" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold font-display">Test your AI agent on a real lead</h3>
          <p className="text-xs text-slate-500 mt-1">Places a live call using your first pending lead's real data (name, interest &amp; language). Listen, then refine your prompt &amp; knowledge base.</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && call()} placeholder="Lead mobile" className="sm:w-48" data-testid="campaign-test-call-input" />
          <Button style={{ background: "#4f46e5" }} onClick={call} disabled={calling} data-testid="campaign-test-call-btn">
            {calling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PhoneCall className="w-4 h-4 mr-1" />}Call lead
          </Button>
        </div>
      </div>

      {lead ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="campaign-test-lead">
          <span className="text-slate-400">Test lead:</span>
          <span className="font-medium text-slate-700 dark:text-slate-200" data-testid="ctl-name">{lead.name || "—"}</span>
          <span className="text-slate-500" data-testid="ctl-mobile">{lead.mobile || "—"}</span>
          {lead.interest && <span className="text-slate-500" data-testid="ctl-interest">· {lead.interest}</span>}
          {lead.city && <span className="text-slate-500">· {lead.city}</span>}
          <span className="px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300" data-testid="ctl-language">{lead.language}</span>
        </div>
      ) : (
        <div className="mt-3 text-xs text-amber-600" data-testid="campaign-test-lead-empty">No pending lead found — import or add a lead to test on real data.</div>
      )}

      {last && <div className={`mt-3 text-xs ${detail?.ok ? "text-emerald-600" : "text-amber-600"}`} data-testid="campaign-test-call-result">{last}</div>}
      {detail && (
        <div className="mt-3 grid md:grid-cols-2 gap-3" data-testid="campaign-test-call-detail">
          <div>
            <div className="text-[11px] font-semibold text-slate-500 mb-1">Request sent to Railway /dial-out</div>
            <pre className="text-[10px] leading-relaxed bg-slate-900 text-slate-100 rounded-md p-2 overflow-x-auto max-h-44" data-testid="campaign-test-call-request">{JSON.stringify(detail.request, null, 2)}</pre>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-slate-500 mb-1">Railway response {detail.status != null && <span className={`px-1.5 py-0.5 rounded-full ${detail.ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>HTTP {detail.status}</span>}</div>
            <pre className="text-[10px] leading-relaxed bg-slate-900 text-slate-100 rounded-md p-2 overflow-x-auto max-h-44" data-testid="campaign-test-call-response">{typeof detail.response === "string" ? detail.response : JSON.stringify(detail.response, null, 2)}</pre>
          </div>
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-400">Requires Plivo Auth Token in Settings. This places a real outbound call.</p>
    </Card>
  );
}

export default function Campaigns() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [sheetsOpen, setSheetsOpen] = useState(false);

  const load = () => api.get("/campaigns").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => {
    load();
    const h = (e) => { if (e.key === "n" && !e.target.closest("input,textarea")) setOpen(true); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const toggle = async (c) => {
    const status = c.status === "active" ? "paused" : "active";
    await api.patch(`/campaigns/${c.id}`, { status });
    toast.success(`Campaign ${status}`);
    load();
  };

  return (
    <div>
      <PageHeader title="Campaigns" subtitle="Manage outbound & inbound calling campaigns">
        <Button variant="outline" onClick={() => setSheetsOpen(true)} data-testid="import-leads-btn"><FileSpreadsheet className="w-4 h-4 mr-1" /> Import Leads (Excel/CSV)</Button>
        <Button onClick={() => setOpen(true)} data-testid="new-campaign-btn"><Plus className="w-4 h-4 mr-1" /> New Campaign</Button>
      </PageHeader>

      <Dialog open={sheetsOpen} onOpenChange={setSheetsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Import Leads from Excel / CSV</DialogTitle></DialogHeader>
          <LeadUploadPanel onImported={() => { load(); setSheetsOpen(false); }} />
        </DialogContent>
      </Dialog>

      <AutoDialerCard />

      <TestCallWidget />

      {!list ? <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">{Array.from({length:3}).map((_,i)=><Skeleton key={i} className="h-48" />)}</div> :
        list.length === 0 ? <Card className="border shadow-none"><EmptyState icon={Plus} title="No campaigns yet" description="Create your first calling campaign to start qualifying leads." action={<Button onClick={() => setOpen(true)}>Create Campaign</Button>} /></Card> :
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((c) => {
            const pct = c.stats.total_leads ? Math.round(c.stats.called / c.stats.total_leads * 100) : 0;
            return (
              <Card key={c.id} className="border shadow-none p-5" data-testid={`campaign-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <DirectionBadge direction={c.direction} />
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${c.status === "active" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15" : "bg-slate-100 text-slate-500 dark:bg-slate-700"}`}>{c.status}</span>
                </div>
                <h3 className="font-semibold font-display mt-3 mb-1">{c.name}</h3>
                <p className="text-xs text-slate-500 line-clamp-2 h-8">{c.description}</p>
                {c.direction !== "inbound" && (
                  <div className="mt-4">
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">{c.stats.called} of {c.stats.total_leads} called</span><span className="font-medium">{pct}%</span></div>
                    <Progress value={pct} className="h-2" />
                  </div>
                )}
                {c.direction !== "outbound" && <div className="mt-4 text-xs text-purple-600 font-medium">{c.stats.inbound_today} inbound received today</div>}
                <div className="flex gap-2 mt-5">
                  <Button size="sm" variant="outline" onClick={() => toggle(c)} data-testid={`toggle-${c.id}`}>{c.status === "active" ? <><Pause className="w-3 h-3 mr-1" />Pause</> : <><Play className="w-3 h-3 mr-1" />Resume</>}</Button>
                  <Button size="sm" variant="ghost"><Users className="w-3 h-3 mr-1" />Leads</Button>
                  <Button size="sm" variant="ghost"><BarChart3 className="w-3 h-3" /></Button>
                </div>
              </Card>
            );
          })}
        </div>
      }

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Create Campaign</DialogTitle></DialogHeader>
          <Wizard onClose={() => setOpen(false)} onCreated={load} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
